import React, { useMemo, useState } from 'react';
import { Card, Typography, Select, Space, Tooltip, Empty } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/tr';

const { Title, Text } = Typography;
const { Option } = Select;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('tr');

const HourlyHeatmapTable = ({ data, loading }) => {
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  // Mevcut metrikleri al
  const availableMetrics = useMemo(() => {
    if (!data || data.length === 0) return [];
    const metrics = new Set();
    data.forEach(item => {
      if (item.data && typeof item.data === 'object') {
        Object.keys(item.data).forEach(key => metrics.add(key));
      }
    });
    return Array.from(metrics);
  }, [data]);

  // Revenue varsa onu seç, yoksa ilk metriği seç
  React.useEffect(() => {
    if (availableMetrics.length > 0) {
      if (availableMetrics.includes('revenue')) {
        setSelectedMetric('revenue');
      } else if (!selectedMetric || !availableMetrics.includes(selectedMetric)) {
        setSelectedMetric(availableMetrics[0]);
      }
    }
  }, [availableMetrics, selectedMetric]);

  // Heatmap verisi hazırla
  const heatmapData = useMemo(() => {
    if (!data || data.length === 0 || !selectedMetric) return {};

    const heatmap = {};
    const values = [];

    data.forEach(item => {
      // UTC'den Türkiye saatine çevir
      const date = dayjs.utc(item.date).tz('Europe/Istanbul');
      const day = date.format('YYYY-MM-DD');
      const hour = date.hour();
      const value = item.data?.[selectedMetric];

      if (value !== undefined && value !== null) {
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          if (!heatmap[day]) {
            heatmap[day] = {};
          }
          heatmap[day][hour] = numValue;
          values.push(numValue);
        }
      }
    });

    // Min/Max değerleri hesapla
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    return {
      data: heatmap,
      minValue,
      maxValue,
      hasData: values.length > 0
    };
  }, [data, selectedMetric]);

  // Renk hesaplama fonksiyonu - Kırmızı-Yeşil geçişi (düşük kırmızı, yüksek yeşil)
  const getHeatmapColor = (value, min, max) => {
    if (value === undefined || value === null) {
      return 'transparent';
    }

    if (min === max) {
      return 'rgba(0, 255, 0, 0.6)'; // Yeşil
    }

    // Normalize et (0-1 arası)
    const normalized = (value - min) / (max - min);
    
    // Kırmızı-Yeşil geçişi (tersine çevrildi)
    let r, g, b;
    
    // Düşük değerler kırmızı, yüksek değerler yeşil
    r = Math.round(255 * (1 - normalized)); // Düşük değerlerde yüksek kırmızı
    g = Math.round(255 * normalized);       // Yüksek değerlerde yüksek yeşil  
    b = 0;
    
    const opacity = 0.3 + (normalized * 0.6); // 0.3 - 0.9 arası opacity
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Saat başlıkları (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Günleri sırala
  const sortedDays = Object.keys(heatmapData.data || {}).sort();

  // Saatlik ortalamalar hesapla
  const hourlyAverages = useMemo(() => {
    if (!heatmapData.data || sortedDays.length === 0) return {};
    
    const averages = {};
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    hours.forEach(hour => {
      const values = [];
      sortedDays.forEach(day => {
        const value = heatmapData.data[day]?.[hour];
        if (value !== undefined && value !== null) {
          values.push(value);
        }
      });
      
      if (values.length > 0) {
        averages[hour] = values.reduce((sum, val) => sum + val, 0) / values.length;
      }
    });
    
    return averages;
  }, [heatmapData.data, sortedDays]);

  if (!heatmapData.hasData) {
    return (
      <Card>
        <Empty 
          description="Seçili metrik için saatlik veri bulunamadı"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Space align="center">
          <Title level={4} style={{ margin: 0 }}>
            Saatlik Isı Haritası
          </Title>
          <Select
            value={selectedMetric}
            onChange={setSelectedMetric}
            style={{ minWidth: 200 }}
            placeholder="Metrik seçin"
          >
            {availableMetrics.map(metric => (
              <Option key={metric} value={metric}>
                {metric}
              </Option>
            ))}
          </Select>
        </Space>
        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
          Kırmızı düşük değerleri, yeşil yüksek değerleri gösterir
        </Text>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '12px'
        }}>
          <thead>
            <tr>
              <th style={{ 
                padding: '8px 4px', 
                border: '1px solid #f0f0f0',
                background: '#fafafa',
                textAlign: 'center',
                minWidth: '80px',
                position: 'sticky',
                left: 0,
                zIndex: 1
              }}>
                Tarih
              </th>
              {hours.map(hour => (
                <th key={hour} style={{ 
                  padding: '8px 4px',
                  border: '1px solid #f0f0f0',
                  background: '#fafafa',
                  textAlign: 'center',
                  minWidth: '30px',
                  fontSize: '11px'
                }}>
                  {hour.toString().padStart(2, '0')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedDays.map(day => (
              <tr key={day}>
                <td style={{ 
                  padding: '4px',
                  border: '1px solid #f0f0f0',
                  background: '#fafafa',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1
                }}>
                  <div>
                    {dayjs(day).format('DD.MM')}
                  </div>
                  <div style={{ fontSize: '10px', color: '#666' }}>
                    {dayjs(day).format('ddd')}
                  </div>
                </td>
                {hours.map(hour => {
                  const value = heatmapData.data[day]?.[hour];
                  const hasValue = value !== undefined && value !== null;
                  
                  return (
                    <td key={hour} style={{ 
                      padding: '2px',
                      border: '1px solid #f0f0f0',
                      textAlign: 'center',
                      background: getHeatmapColor(value, heatmapData.minValue, heatmapData.maxValue),
                      cursor: hasValue ? 'pointer' : 'default'
                    }}>
                      {hasValue ? (
                        <Tooltip 
                          title={
                            <div>
                              <div><strong>{selectedMetric}</strong></div>
                              <div>Tarih: {dayjs(day).format('DD.MM.YYYY')} {hour.toString().padStart(2, '0')}:00</div>
                              <div>Değer: {(Math.round(value * 100) / 100).toLocaleString('tr-TR')}</div>
                            </div>
                          }
                        >
                          <div style={{ 
                            padding: '4px 2px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: value > (heatmapData.minValue + heatmapData.maxValue) / 2 ? 'white' : '#333'
                          }}>
                            {value >= 1000 ? `${Math.round(value/1000)}k` : Math.round(value * 100) / 100}
                          </div>
                        </Tooltip>
                      ) : (
                        <div style={{ padding: '4px 2px', fontSize: '10px' }}>
                          -
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            
            {/* Ortalama Satırı */}
            {Object.keys(hourlyAverages).length > 0 && (
              <tr style={{ borderTop: '2px solid #1890ff' }}>
                <td style={{ 
                  padding: '4px',
                  border: '1px solid #f0f0f0',
                  background: '#e6f7ff',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1
                }}>
                  <div style={{ color: '#1890ff' }}>
                    Ortalama
                  </div>
                </td>
                {hours.map(hour => {
                  const avgValue = hourlyAverages[hour];
                  const hasAvg = avgValue !== undefined && avgValue !== null;
                  
                  return (
                    <td key={hour} style={{ 
                      padding: '2px',
                      border: '1px solid #f0f0f0',
                      textAlign: 'center',
                      background: hasAvg ? 
                        getHeatmapColor(avgValue, heatmapData.minValue, heatmapData.maxValue) :
                        '#f5f5f5',
                      cursor: hasAvg ? 'pointer' : 'default'
                    }}>
                      {hasAvg ? (
                        <Tooltip 
                          title={
                            <div>
                              <div><strong>{selectedMetric} - Ortalama</strong></div>
                              <div>Saat: {hour.toString().padStart(2, '0')}:00</div>
                              <div>Ortalama: {(Math.round(avgValue * 100) / 100).toLocaleString('tr-TR', { 
                                minimumFractionDigits: 1, 
                                maximumFractionDigits: 2 
                              })}</div>
                            </div>
                          }
                        >
                          <div style={{ 
                            padding: '4px 2px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: '#1890ff'
                          }}>
                            {avgValue >= 1000 ? `${Math.round(avgValue/1000)}k` : Math.round(avgValue * 100) / 100}
                          </div>
                        </Tooltip>
                      ) : (
                        <div style={{ padding: '4px 2px', fontSize: '10px' }}>
                          -
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Renk skalası */}
      <div style={{ 
        marginTop: 16, 
        padding: '12px',
        background: '#fafafa',
        borderRadius: '6px'
      }}>
        <Text strong style={{ fontSize: '12px' }}>Renk Skalası: </Text>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          marginTop: '4px'
        }}>
          <Text style={{ fontSize: '11px' }}>Min ({heatmapData.minValue?.toLocaleString()})</Text>
          <div style={{ 
            width: '100px', 
            height: '12px',
            background: 'linear-gradient(to right, rgba(255, 0, 0, 0.7), rgba(255, 255, 0, 0.7), rgba(0, 255, 0, 0.9))',
            border: '1px solid #d9d9d9',
            borderRadius: '2px'
          }} />
          <Text style={{ fontSize: '11px' }}>Max ({heatmapData.maxValue?.toLocaleString()})</Text>
        </div>
      </div>
    </Card>
  );
};

export default HourlyHeatmapTable;